/**
 * @file ColorBar.js
 * @description A React component that displays a color bar with customizable colors and options.
 */

import React, { useEffect, useState } from 'react';
import SimpleDrawer from './SimpleDrawer';
/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import PropTypes from 'prop-types';
import style from './ThemePage.module.scss';
import Tooltip from '../ui/tooltips/Tooltip';
import { getBrightness, getContrastColor } from '../../utils/utils';
import { getText } from '../../i18n';

/**
 * ColorBar component
 * @param {Object} props - Component props
 * @param {string} [props.label] - Label for the color bar
 * @param {Object} props.initialColors - Initial colors for the color bar
 * @param {Function} props.onChangeComplete - Callback function to handle color change
 * @param {boolean} [props.allowGradient=true] - Flag to allow gradient colors
 * @param {string} [props.toolTipText] - Text for the tooltip
 * @param {boolean} [props.readOnly] - Flag for editing
 * @param {boolean} [props.startBelowHeader=true]  - Flag to style the drawer to start below header
 * @returns {JSX.Element} ColorBar component
 */
const ColorBar = ({
    label,
    initialColors,
    onChangeComplete,
    allowGradient = true,
    toolTipText,
    readOnly = false,
    startBelowHeader,
}) => {
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [colors, setColors] = useState(initialColors);

    useEffect(() => {
        setColors(initialColors);
    }, [initialColors]);

    const textColorHex = getContrastColor(colors.left);
    const textColorPencil = getContrastColor(colors.right);

    const barStyle = css`
        height: 40px;
        background: ${colors.type === 'gradient'
            ? `linear-gradient(to right, ${colors.left}, ${colors.right})`
            : colors.left};
        margin: 10px auto;
        border: 1px solid var(--alpha-black-20, rgba(47, 43, 67, 0.2));
        box-shadow:
            0px -1px 0px 0px rgba(47, 43, 67, 0.1) inset,
            0px 1px 3px 0px rgba(47, 43, 67, 0.1);
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 0 15px;
    `;

    const handleColorChange = (type, updatedColors) => {
        setColors({ ...colors, ...updatedColors, type });
        onChangeComplete({ ...colors, ...updatedColors, type });
    };

    return (
        <div className={`w-100`}>
            {label && (
                <label className="mb-2">
                    {label}
                    {toolTipText && (
                        <sup>
                            <Tooltip html={toolTipText} placement="right">
                                <i
                                    className="bi bi-info-circle"
                                    style={{
                                        paddingLeft: '5px',
                                        fontSize: '13px',
                                        color: '#1366ff',
                                    }}
                                ></i>
                            </Tooltip>
                        </sup>
                    )}
                </label>
            )}
            <div className={`${style.colorBar}`} css={barStyle}>
                <span style={{ color: textColorHex }}>
                    {getText('msg_887bb1c74100')} {colors.left}
                    {colors.left !== colors.right && ` / ${colors.right}`}
                </span>

                {!readOnly && (
                    <i
                        className="bi bi-pencil"
                        style={{ color: textColorPencil, cursor: 'pointer' }}
                        onClick={() => setIsDrawerOpen(true)}
                    />
                )}
            </div>
            <SimpleDrawer
                isOpen={isDrawerOpen}
                closeDrawer={() => setIsDrawerOpen(false)}
                onColorChange={handleColorChange}
                initialColors={colors}
                drawerType={colors.type}
                allowGradient={allowGradient}
                startBelowHeader={startBelowHeader}
            />
        </div>
    );
};

ColorBar.propTypes = {
    // label: PropTypes.string.isRequired,
    initialColors: PropTypes.object.isRequired,
    onChangeComplete: PropTypes.func.isRequired,
    allowGradient: PropTypes.bool,
};

export default ColorBar;
